// How an expanded row's detail enters and leaves the table flow. Its height and opacity ease over
// DETAIL_MS with the house easing, so the rows below move continuously instead of jumping; a
// reduced-motion preference presents and removes the detail at once.

import { useCallback, useState } from 'react'

export const DETAIL_MS = 160
// the transition's own end event is the signal; this margin only covers a missed event
const SETTLE_MARGIN_MS = 60
const REDUCE = '(prefers-reduced-motion: reduce)'

export const reducedMotion = () =>
  typeof window.matchMedia !== 'function' || window.matchMedia(REDUCE).matches

const noop = () => {}

/**
 * Keeps a closing detail in the document until its exit finishes.
 * @param {boolean} open
 * @returns {{present: boolean, leaving: boolean, onExited: () => void}}
 */
export const useDetailPresence = open => {
  const [present, setPresent] = useState(open)
  if (open && !present) setPresent(true)
  if (!open && present && reducedMotion()) setPresent(false)
  const onExited = useCallback(() => setPresent(false), [])
  return { present: open || present, leaving: !open && present, onExited }
}

/**
 * Eases the clip element from its current height to its content height (entering) or to zero
 * (leaving). Returns a cancel function that leaves the element where it is, so a reversed toggle
 * continues from the height it reached.
 * @param {?HTMLElement} el the detail's clip element
 * @param {{entering: boolean, leaving: boolean, onDone: () => void}} motion
 * @returns {() => void}
 */
export const animateDetail = (el, { entering, leaving, onDone }) => {
  if (!el) {
    onDone()
    return noop
  }
  const start = entering ? 0 : el.getBoundingClientRect().height
  const end = leaving ? 0 : el.scrollHeight
  if (reducedMotion() || start === end) {
    if (!leaving) {
      el.style.height = ''
      delete el.dataset.motion
    }
    onDone()
    return noop
  }

  el.style.height = `${start}px`
  if (entering) el.dataset.motion = 'hidden'
  // commit the starting frame so the browser transitions from it
  void el.offsetHeight // eslint-disable-line no-void
  el.style.height = `${end}px`
  if (leaving) el.dataset.motion = 'hidden'
  else delete el.dataset.motion

  let timer = null
  let settled = false
  const detach = () => {
    clearTimeout(timer)
    el.removeEventListener('transitionend', onEnd) // eslint-disable-line no-use-before-define
  }
  const settle = () => {
    if (settled) return
    settled = true
    detach()
    if (!leaving) el.style.height = ''
    onDone()
  }
  function onEnd(event) {
    if (event.target === el && event.propertyName === 'height') settle()
  }
  el.addEventListener('transitionend', onEnd)
  timer = setTimeout(settle, DETAIL_MS + SETTLE_MARGIN_MS)
  return () => {
    settled = true
    detach()
  }
}
